const siteUrl = 'https://smartcan.ameriuse.com';
const defaultTitle = 'SmartCAN - CAN Bus Analyzer, Live Capture & Decode Platform';
const defaultDescription = 'Live CAN bus analysis, ECU data decoding, and community-powered signal database.';
const defaultImage = `${siteUrl}/og-smartcan-live.png`;

type SeoEntry = {
  title: string;
  description: string;
};

type JsonLdObject = Record<string, unknown>;

export const routeSeo: Record<string, SeoEntry> = {
  '/': {
    title: 'SmartCAN - CAN Bus Analysis Platform',
    description: 'SmartCAN is a CAN bus analyzer for live capture, ECU analysis, CAN log review, and community-powered signal hints.'
  },
  '/live': {
    title: 'Live CAN Bus Monitor - SmartCAN',
    description: 'Monitor CAN bus frames in real time from SmartCAN hardware over Wi-Fi with browser capture, filtering, export, and demo mode.'
  },
  '/analyzer': {
    title: 'CAN Log Analyzer - SmartCAN',
    description: 'Upload CAN logs, inspect CAN IDs and data bytes, filter captures, export CSV or JSON, and find community signal hints.'
  },
  '/decode': {
    title: 'Community CAN Signals - SmartCAN',
    description: 'Browse verified community CAN signal hints for ECU analysis, embedded systems, automotive data, and CAN decoder workflows.'
  },
  '/docs': {
    title: 'SmartCAN Documentation',
    description: 'SmartCAN documentation for live monitoring, CAN log analysis, device setup, VS Code extension, Python SDK, and troubleshooting.'
  },
  '/what-is-smartcan': {
    title: 'What Is SmartCAN? CAN Bus Analyzer Guide',
    description: 'Learn how SmartCAN helps with CAN bus analysis, AI-assisted decoding, OBD data, and vehicle diagnostics workflows.'
  },
  '/best-can-bus-tools': {
    title: 'Best CAN Bus Tools for Analysis',
    description: 'Compare CAN log viewers, DBC decoders, OBD scanners, reverse engineering tools, and AI-assisted CAN analyzers.'
  },
  '/smartcan-vs-alternatives': {
    title: 'SmartCAN vs Traditional CAN Tools',
    description: 'Compare SmartCAN with traditional CAN tools for setup, AI assistance, OBD workflows, and CAN log interpretation.'
  },
  '/can-bus-decoder-guide': {
    title: 'CAN Bus Decoder Guide',
    description: 'Learn CAN IDs, payload bytes, signals, scaling, offsets, DBC files, and how SmartCAN helps organize CAN data.'
  }
};

const faqData: Record<string, Array<{ question: string; answer: string }>> = {
  '/what-is-smartcan': [
    { question: 'What is SmartCAN?', answer: 'SmartCAN is a CAN bus analyzer and decoder platform for live capture, CAN log analysis, OBD data interpretation, and community signal hints.' },
    { question: 'Is SmartCAN only for cars?', answer: 'No. SmartCAN is useful for automotive diagnostics, embedded systems, robotics, industrial CAN networks, and reverse engineering workflows.' },
    { question: 'What is CAN decoding?', answer: 'CAN decoding is the process of turning raw CAN IDs and payload bytes into useful signal names, values, units, and context.' },
    { question: 'Can SmartCAN decode every CAN signal automatically?', answer: 'No. SmartCAN can assist with analysis and verified hints, but accurate numeric decoding requires signal definitions, formulas, evidence, and validation.' },
    { question: 'Does SmartCAN support OBD data analysis?', answer: 'SmartCAN includes generic OBD-II response decoding for known values such as RPM and speed, and the workflow can assist with OBD data analysis.' },
    { question: 'How does AI-assisted CAN analysis help?', answer: 'AI-assisted analysis can help organize observations, identify candidate signals, and explain CAN data patterns, while verified definitions remain evidence-based.' },
    { question: 'Can I upload CAN logs?', answer: 'Yes. The Web Analyzer can inspect uploaded CAN logs in the browser and export parsed frames as CSV or JSON.' },
    { question: 'Can users contribute signal hints?', answer: 'Yes. Users can submit candidate signal hints, which are reviewed before becoming part of the verified signal catalog.' }
  ],
  '/can-bus-decoder-guide': [
    { question: 'What is a CAN bus decoder?', answer: 'A CAN bus decoder maps raw CAN frames into meaningful signals using message IDs, bit positions, scaling, offsets, units, and context.' },
    { question: 'What is a CAN ID?', answer: 'A CAN ID identifies the message on the bus. The same ID can have different meaning depending on vehicle, ECU, bus, or embedded system.' },
    { question: 'What is a CAN payload?', answer: 'A payload is the data bytes inside a CAN frame. Signals are often packed into specific bits or byte ranges within the payload.' },
    { question: 'What is a DBC file?', answer: 'A DBC file describes CAN messages and signals, including IDs, names, bit positions, scaling, offsets, units, and value ranges.' },
    { question: 'Why is CAN decoding hard?', answer: 'Many signals are manufacturer-specific and context-dependent, so raw frames often need evidence, repeatable tests, and validated definitions.' },
    { question: 'Can SmartCAN replace a DBC file?', answer: 'SmartCAN can help inspect and organize CAN data, but precise numeric decoding still benefits from DBC-style signal definitions.' },
    { question: 'Does SmartCAN show community signal hints?', answer: 'Yes. Verified community signals can appear as candidate hints when matching CAN IDs are observed.' },
    { question: 'Can SmartCAN decode OBD-II responses?', answer: 'SmartCAN includes generic OBD-II decoding for supported response frames such as engine RPM and vehicle speed.' }
  ],
  '/best-can-bus-tools': [
    { question: 'What makes a good CAN Bus Analyzer?', answer: 'A good CAN Bus Analyzer preserves raw frames, supports filtering, shows timing and IDs clearly, and helps export data for deeper analysis.' },
    { question: 'Is SmartCAN a replacement for every CAN tool?', answer: 'No. SmartCAN is a modern option for live capture, CAN log analysis, OBD interpretation, and AI-assisted workflows, while traditional tools remain valuable for expert benches.' },
    { question: 'Does SmartCAN support CAN log analysis?', answer: 'Yes. The Web Analyzer can inspect uploaded logs, show CAN ID summaries, filter data bytes, and export CSV or JSON.' }
  ],
  '/smartcan-vs-alternatives': [
    { question: 'How is SmartCAN different from traditional CAN tools?', answer: 'Traditional tools are powerful for experts, while SmartCAN focuses on fast interpretation, workflow, and AI-assisted analysis.' },
    { question: 'Can beginners use SmartCAN?', answer: 'SmartCAN is designed to be approachable with browser tools, demo mode, readable tables, and community signal hints.' },
    { question: 'Does SmartCAN still show raw data?', answer: 'Yes. SmartCAN keeps raw CAN IDs, DLC, payload bytes, and timestamps visible so users can validate analysis.' }
  ]
};

function breadcrumbSchema(pathname: string, pageName: string): JsonLdObject {
  const path = pathname.replace(/\/$/, '') || '/';
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'SmartCAN',
        item: siteUrl
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: pageName,
        item: `${siteUrl}${path}`
      }
    ]
  };
}

function faqSchema(pathname: string): JsonLdObject | null {
  const faqs = faqData[normalizePath(pathname)];
  if (!faqs) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

export function getStructuredDataForPath(pathname: string) {
  const normalized = normalizePath(pathname);
  const seo = getSeoForPath(pathname);
  if (!['/what-is-smartcan', '/best-can-bus-tools', '/smartcan-vs-alternatives', '/can-bus-decoder-guide'].includes(normalized)) {
    return [];
  }

  return [
    breadcrumbSchema(normalized, seo.title),
    faqSchema(normalized)
  ].filter((schema): schema is JsonLdObject => Boolean(schema));
}

function normalizePath(pathname: string) {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (normalized.startsWith('/docs/')) return '/docs';
  return normalized;
}

function upsertMeta(selector: string, create: () => HTMLMetaElement, value: string, attribute = 'content') {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

function upsertCanonical(url: string) {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = url;
}

function upsertRouteSchema(pathname: string) {
  const schemas = getStructuredDataForPath(pathname);
  let element = document.head.querySelector('#route-jsonld') as HTMLScriptElement | null;

  if (schemas.length === 0) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('script');
    element.id = 'route-jsonld';
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
}

export function getSeoForPath(pathname: string) {
  return routeSeo[normalizePath(pathname)] ?? {
    title: defaultTitle,
    description: defaultDescription
  };
}

export function applyRouteSeo(pathname: string) {
  const path = pathname.replace(/\/$/, '') || '/';
  const url = `${siteUrl}${path === '/' ? '' : path}`;
  const seo = getSeoForPath(pathname);

  document.title = seo.title;
  upsertCanonical(url);
  upsertMeta('meta[name="description"]', () => {
    const meta = document.createElement('meta');
    meta.name = 'description';
    return meta;
  }, seo.description);
  upsertMeta('meta[property="og:title"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    return meta;
  }, seo.title);
  upsertMeta('meta[property="og:description"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:description');
    return meta;
  }, seo.description);
  upsertMeta('meta[property="og:url"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:url');
    return meta;
  }, url);
  upsertMeta('meta[property="og:image"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:image');
    return meta;
  }, defaultImage);
  upsertMeta('meta[name="twitter:title"]', () => {
    const meta = document.createElement('meta');
    meta.name = 'twitter:title';
    return meta;
  }, seo.title);
  upsertMeta('meta[name="twitter:description"]', () => {
    const meta = document.createElement('meta');
    meta.name = 'twitter:description';
    return meta;
  }, seo.description);
  upsertMeta('meta[name="twitter:image"]', () => {
    const meta = document.createElement('meta');
    meta.name = 'twitter:image';
    return meta;
  }, defaultImage);
  upsertRouteSchema(pathname);
}
